import type { CaptureResult } from "posthog-js";

import {
  rebuildBoundedEvent,
  scrubSensitiveLocationProperties,
} from "@sill/shared/observability/posthog-before-send";
import {
  currentBrowserLocation,
  hasSensitiveClaimLocation,
  isSensitiveClaimLocation,
} from "@sill/shared/observability/sensitive-location";

const EXPLICIT_CLAIM_EVENTS = new Set(["claim_completed", "claim_resumed"]);
const CLAIM_TIERS = new Set(["t1", "t2", "t3"]);

function boundedClaimProperties(event: string, properties: CaptureResult["properties"]): CaptureResult["properties"] | null {
  if (event === "claim_resumed" && typeof properties.tier === "string" && CLAIM_TIERS.has(properties.tier)) {
    return { tier: properties.tier };
  }
  if (event === "claim_completed" && typeof properties.in_area === "boolean") {
    return { in_area: properties.in_area };
  }
  return null;
}

/**
 * PostHog's supported `before_send` hook for the sensitive claim funnel.
 * Rebuilt events keep PostHog's ingestion keys (`rebuildBoundedEvent`):
 * posthog-js drops an event whose `token` a hook removed.
 */
export function filterClaimTelemetry(
  event: CaptureResult | null,
  location: string | undefined = currentBrowserLocation(),
): CaptureResult | null {
  if (event === null) return null;

  // The share card can render on an ordinary authenticated route, where PostHog
  // automatically adds browser URL/referrer properties. Rebuild it here so its
  // public contract stays exactly {kind:"buyer_brag"} everywhere.
  if (event.event === "share_card_rendered") {
    return event.properties.kind === "buyer_brag"
      ? rebuildBoundedEvent(event, { kind: "buyer_brag" })
      : null;
  }

  if (EXPLICIT_CLAIM_EVENTS.has(event.event)) {
    const properties = boundedClaimProperties(event.event, event.properties);
    return properties === null ? null : rebuildBoundedEvent(event, properties);
  }

  if (isSensitiveClaimLocation(location) || hasSensitiveClaimLocation(event.properties)) {
    return null;
  }
  // A session that BEGAN on a credential route stamps it onto every later
  // event ($session_entry_url, $set_once.$initial_current_url, ...).
  return scrubSensitiveLocationProperties(event);
}
